import { cva } from 'class-variance-authority';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { Avatar, Style } from '@dicebear/core';
import thumbsDefinition from '@dicebear/styles/thumbs.json';
import { format, isSameYear, isSameWeek } from 'date-fns';
import { toast } from 'sonner';
import {
  ENRICH_BALANCE_ACCOUNT_TYPE,
  ENRICH_REWARDS_ACCOUNT_TYPE,
  ENRICH_REWARDS_NICKNAME,
  LOYALTY_PROGRAM_ACCOUNT_TYPE,
  POINTS_PER_DOLLAR,
  RESERVED_ACCOUNT_NICKNAMES,
} from './constants';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getDayTimePeriod() {
  const hour = new Date().getHours();
  if (hour >= 6 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 18) return 'afternoon';
  return 'evening';
}

export const indexColorMap = {
  index: {
    0: 'bg-primary-light',
    1: 'bg-primary-blue-mid',
    2: 'bg-primary-yellow-light',
    3: 'bg-secondary-mid',
    4: 'bg-primary-sea-green-mid',
    5: 'bg-primary-blue-light',
    6: 'bg-primary-yellow-mid',
    7: 'bg-secondary-light',
    8: 'bg-primary-olive-green-light',
    9: 'bg-primary',
    10: 'bg-secondary-notice',
    11: 'bg-secondary',
  },
};

export const coloredTileVariants = cva('text-4xl leading-10 text-foreground opacity-80', {
  variants: indexColorMap,
});

export type ColoredTileIndex = keyof typeof indexColorMap.index;

const thumbsStyle = new Style(thumbsDefinition);

export function getBenefitAvatar(seed: string) {
  return new Avatar(thumbsStyle, {
    seed,
    size: 100,
  }).toDataUri();
}

/**
 * Converts an enrich_rewards reserve balance (stored in USD) into display
 * points. A $1.25 reserve balance becomes 1,250 points. Returns 0 for nullish
 * or non-numeric input.
 */
export function dollarsToPoints(value: number | string | null | undefined): number {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (num === null || num === undefined || isNaN(num)) return 0;
  return Math.round(num * POINTS_PER_DOLLAR);
}

export function formatCurrency(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '$0.00';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(num);
}

export const toMMDDYYYY = (date?: string | null) => {
  if (!date) return '';
  return format(new Date(date), 'MM/dd/yyyy');
};

// dateString is in the format of yyyy MMM d
export function formatyyyy_MMM_dToISO(dateString: string) {
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const [year, month, day] = dateString.split(' ');
  const date = new Date();
  date.setFullYear(parseInt(year));
  date.setMonth(months.indexOf(month));
  date.setDate(parseInt(day));
  return date.toISOString();
}

export function dateMonthORDateMonthYear(date: string) {
  try {
    const today = new Date();
    const dateObj = new Date(date);
    if (isSameYear(dateObj, today)) {
      return format(dateObj, 'MMMM d');
    }
    return format(dateObj, 'MMM d, yyyy');
  } catch {
    return date;
  }
}

export function monthOrMonthYear(dateObj: Date) {
  const today = new Date();
  if (isSameYear(dateObj, today)) {
    return format(dateObj, 'MMMM');
  }
  return format(dateObj, 'MMMM yyyy');
}

export function formatDateToDayIfThisWeek(date: string) {
  const today = new Date();
  const transactionDate = new Date(date);
  if (isSameWeek(transactionDate, today)) {
    return format(transactionDate, 'EEEE');
  }
  return dateMonthORDateMonthYear(date);
}

export const removeUnderscores = (value: string | undefined | null) => value?.split('_').join(' ');

export function parseDateAsLocal(dateString: string): Date {
  const datePart = dateString.split('T')[0];
  const [year, month, day] = datePart.split('-').map(Number);
  switch (true) {
    case !!year && !!month && !!day:
      return new Date(year, month - 1, day);
    case !!year && !!month:
      return new Date(year, month - 1, 1);
    case !!year:
      return new Date(year, 0, 1);
    default:
      return new Date();
  }
}

const pr = new Intl.PluralRules('en-US', { type: 'ordinal' });

const suffixes = new Map([
  ['one', 'st'],
  ['two', 'nd'],
  ['few', 'rd'],
  ['other', 'th'],
]);
export function formatOrdinals(n: number) {
  const rule = pr.select(n);
  const suffix = suffixes.get(rule);
  return `${n}${suffix}`;
}

/**
 * Determines if an account can be renamed.
 * Enrich Balance and Enrich Rewards accounts cannot be renamed.
 */
export function isAccountRenameable(
  account: { type: string; nickname: string } | undefined,
): boolean {
  if (!account) return false;

  // Enrich Balance and Enrich Rewards (type) accounts cannot be renamed
  if (
    account.type === ENRICH_BALANCE_ACCOUNT_TYPE ||
    account.type === ENRICH_REWARDS_ACCOUNT_TYPE
  ) {
    return false;
  }

  // Legacy "Enrich Rewards" loyalty program account cannot be renamed (backend
  // still rejects this rename; keep the UI consistent during the migration).
  if (
    account.type === LOYALTY_PROGRAM_ACCOUNT_TYPE &&
    account.nickname === ENRICH_REWARDS_NICKNAME
  ) {
    return false;
  }

  return true;
}

/**
 * Checks if a nickname is reserved and cannot be used for any account.
 * Reserved names: "Enrich Balance", "Enrich Rewards"
 */
export function isReservedAccountNickname(nickname: string): boolean {
  return RESERVED_ACCOUNT_NICKNAMES.includes(nickname);
}

export async function copyToClipboard(text: string, label: string): Promise<boolean> {
  if (typeof navigator === 'undefined' || !navigator.clipboard || !navigator.clipboard.writeText) {
    toast.error('Failed to copy to clipboard.');
    return false;
  }
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
    return true;
  } catch {
    toast.error('Failed to copy to clipboard.');
    return false;
  }
}

export function formatDateTimeLocal(date: Date | undefined): string {
  if (!date) return '';
  const offset = date.getTimezoneOffset() * 60000;
  const localDate = new Date(date.getTime() - offset);
  return localDate.toISOString().slice(0, 16);
}

/**
 * Normalizes a tag name to the canonical form the backend accepts: lowercase,
 * with any run of non-alphanumeric characters collapsed to a single hyphen and
 * leading/trailing hyphens stripped. Returns '' if nothing usable remains.
 *
 *   "HELLO"               -> "hello"
 *   "this is hello$"      -> "this-is-hello"
 *   "**hearts99994%^!u"   -> "hearts99994-u"
 */
export function normalizeTagName(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Formats an Enrich Rewards USD value as unsigned points, e.g. "5,000 pts". */
export function formatPoints(value: string | number | null | undefined): string {
  return `${dollarsToPoints(value).toLocaleString('en-US')} pts`;
}

/** Formats an Enrich Rewards USD value as signed points, e.g. "+2,000 pts" / "-1,000 pts". */
export function formatSignedPoints(value: string | number | null | undefined): string {
  const points = dollarsToPoints(value);
  const prefix = points > 0 ? '+' : '';
  return `${prefix}${points.toLocaleString('en-US')} pts`;
}

/**
 * Removes `//` line comments and block comments from a JSONC string so the
 * result can be parsed by `JSON.parse`. String-aware: comment-like sequences
 * inside string values (e.g. URLs) are preserved, and escaped quotes are
 * handled. Comment-free input is returned unchanged.
 */
export function stripJsonComments(input: string): string {
  let out = '';
  let inString = false;
  for (let i = 0; i < input.length; i++) {
    const char = input[i];
    const next = input[i + 1];

    if (inString) {
      out += char;
      if (char === '\\') {
        // Copy the escaped character verbatim so an escaped quote (\") doesn't
        // end the string.
        out += input[i + 1] ?? '';
        i++;
      } else if (char === '"') {
        inString = false;
      }
      continue;
    }

    if (char === '"') {
      inString = true;
      out += char;
    } else if (char === '/' && next === '/') {
      // Line comment: skip until (but not including) the newline.
      i++;
      while (i + 1 < input.length && input[i + 1] !== '\n') {
        i++;
      }
    } else if (char === '/' && next === '*') {
      // Block comment: skip until the closing */.
      i += 2;
      while (i + 1 < input.length && !(input[i] === '*' && input[i + 1] === '/')) {
        i++;
      }
      i++;
    } else {
      out += char;
    }
  }
  return out;
}
